import React from 'react';
import { gameLogger, LogLevel } from '../../../utils/GameLogger';
import { Button } from '../../../game/ui/primitives';

interface LogViewerToolbarProps {
  filter: { level: LogLevel; category: string; search: string };
  setFilter: (f: any) => void;
  autoRefresh: boolean;
  setAutoRefresh: (v: boolean) => void;
  refreshLogs: () => void;
  handleDownload: (format: 'text' | 'json') => void;
  handleCopy: (mode: 'all' | 'filtered' | 'warn-error' | 'error') => void;
  handleClearLogs: () => void;
  handleToggleLogging: () => void;
}

export const LogViewerToolbar: React.FC<LogViewerToolbarProps> = ({
  filter, setFilter, autoRefresh, setAutoRefresh, refreshLogs,
  handleDownload, handleCopy, handleClearLogs, handleToggleLogging
}) => {
  return (
    <div style={{ padding: '12px', borderBottom: '1px solid rgba(255,255,255,0.05)', backgroundColor: 'rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="🔍 Search logs..."
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          style={{ flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '8px 12px', color: 'white', fontSize: '12px', outline: 'none' }}
        />
        <select
          value={filter.level}
          onChange={(e) => setFilter({ ...filter, level: parseInt(e.target.value) })}
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '8px', color: 'white', fontSize: '12px', outline: 'none', cursor: 'pointer' }}
        >
          <option value={LogLevel.DEBUG}>All Levels</option>
          <option value={LogLevel.INFO}>Info+</option>
          <option value={LogLevel.WARN}>Warn+</option>
          <option value={LogLevel.ERROR}>Error</option>
        </select>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#b8c2cc', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} style={{ marginRight: '6px', cursor: 'pointer' }} />
            Auto-scroll
          </label>
        </div>
      </div>
      {/* Category Quick-Filter Chips */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {(['network', 'auth', 'combat', 'map', 'physics', 'test'] as const).map(cat => {
          const isActive = filter.category === cat;
          return (
            <button
              key={cat}
              onClick={() => setFilter({ ...filter, category: isActive ? '' : cat })}
              style={{
                background: isActive ? 'rgba(52, 152, 219, 0.35)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isActive ? '#3498db' : 'rgba(255,255,255,0.12)'}`,
                borderRadius: '12px',
                padding: '3px 10px',
                color: isActive ? '#3498db' : '#888',
                fontSize: '11px',
                cursor: 'pointer',
                fontFamily: 'monospace',
                transition: 'all 0.15s',
              }}
            >
              {cat}
            </button>
          );
        })}
        {filter.category && !['network','auth','combat','map','physics','test'].includes(filter.category) && (
          <button
            onClick={() => setFilter({ ...filter, category: '' })}
            style={{ background: 'rgba(231,76,60,0.2)', border: '1px solid #e74c3c', borderRadius: '12px', padding: '3px 10px', color: '#e74c3c', fontSize: '11px', cursor: 'pointer', fontFamily: 'monospace' }}
          >
            {filter.category} ✕
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={refreshLogs}>Refresh</Button>
        <Button size="sm" variant={gameLogger.isLoggingActive() ? 'danger' : 'success'} onClick={handleToggleLogging}>
          {gameLogger.isLoggingActive() ? 'Stop' : 'Start'}
        </Button>
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
        <Button size="sm" variant="secondary" onClick={() => handleDownload('text')}>.TXT</Button>
        <Button size="sm" variant="secondary" onClick={() => handleDownload('json')}>.JSON</Button>
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
        <Button size="sm" variant="secondary" onClick={() => handleCopy('filtered')}>Copy</Button>
        <Button size="sm" variant="secondary" onClick={() => handleCopy('error')}>Copy Errors</Button>
        <div style={{ flex: 1 }} />
        <Button size="sm" variant="danger" onClick={handleClearLogs}>Clear</Button>
      </div>
    </div>
  );
};
